import { useCallback, useRef } from "react";
import { useForm } from "react-hook-form";

import { Box, Stack, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

import { useGetOtherStructureProtectionPriceLazyQuery } from "@covertree/new-business-graphql";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { Button, Form, FormCurrencyField, FormTextField } from "@covertree/ui";

import { mineSubsidenceScheduledValidation } from "../../validatorsAndTypes/extraCoveragesForm";
import { formatCurrency } from "@covertree/utils";
import { ModalTitle, OtherStructure, OtherStructureWithPrice } from "./types";

interface MineSubsidenceScheduledAddOrEditOtherStructureProps {
  addOrUpdateItem(value: OtherStructureWithPrice): void;
  changeFormVisibilityAndCloseModal(shouldClose: boolean): void;
  currentItem?: OtherStructure;
  updateModalTitle(title: ModalTitle): void;
}

function MineSubsidenceScheduledAddOrEditOtherStructure({
  addOrUpdateItem,
  changeFormVisibilityAndCloseModal,
  currentItem,
  updateModalTitle,
}: MineSubsidenceScheduledAddOrEditOtherStructureProps) {
  const { policyLocator } = useNewBusinessSelector(selectPolicy)!;

  const abortController = useRef(new AbortController());

  const [getOtherStructureProtectionPrice, { loading, data, error }] =
    useGetOtherStructureProtectionPriceLazyQuery({
      context: {
        fetchOptions: {
          signal: abortController.current.signal,
        },
      },
    });

  const {
    control,
    handleSubmit: submit,
    formState: { isDirty, isValid },
    reset,
    getValues,
  } = useForm<OtherStructureWithPrice>({
    mode: "all",
    defaultValues: currentItem ?? {
      description: "",
    },
    // @ts-expect-error Not idea why it errors out...
    resolver: yupResolver(mineSubsidenceScheduledValidation),
  });

  const handleAddItem = useCallback(() => {
    addOrUpdateItem(getValues());
    updateModalTitle(ModalTitle.OtherStructureAdded);
    changeFormVisibilityAndCloseModal(false);
  }, [addOrUpdateItem, getValues]);

  const handleSubmit = useCallback(
    async (otherStructure: OtherStructure) => {
      const { data } = await getOtherStructureProtectionPrice({
        variables: {
          otherStructureDescription: otherStructure.description,
          otherStructureValue: otherStructure.value,
          policyLocator,
        },
      });

      reset({
        ...otherStructure,
        price: data?.getOtherStructureProtectionPrice,
      });
    },
    [getOtherStructureProtectionPrice, policyLocator, reset]
  );

  return (
    <Form onSubmit={submit(handleSubmit)}>
      <Stack spacing={2} mb={2}>
        <Stack direction="column" spacing={2}>
          <FormCurrencyField
            control={control}
            disabled={loading}
            name="value"
            label="Other structure limit"
            id="other-structure-limit"
            dataTestId="other-structure-limit"
          />
          <FormTextField
            control={control}
            disabled={loading}
            name="description"
            dataTestId="other-structure-description"
            id="other-structure-description"
            label="Other structure description"
          />
        </Stack>
        <Box>
          {error && (
            <Box
              sx={(theme) => ({
                padding: theme.spacing(2),
                width: "100%",
              })}>
              <Typography variant="h6" color="error" align="center">
                Something went wrong, while getting Other Structure&apos;s
                price. Please try again.
              </Typography>
            </Box>
          )}
          {data?.getOtherStructureProtectionPrice && (
            <Box
              sx={(theme) => ({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: theme.spacing(16),
                backgroundColor: "secondary.contrastText",
                borderRadius: theme.shape.borderRadius,
              })}>
              <Box sx={{ width: "100%" }}>
                <Typography variant="h3" color="secondary" align="center">
                  {formatCurrency(
                    data?.getOtherStructureProtectionPrice / 12,
                    2
                  )}
                </Typography>
                <Typography variant="h6" align="center">
                  Premium monthly
                </Typography>
                <Typography variant="subtitle1" align="center">
                  ({formatCurrency(data?.getOtherStructureProtectionPrice, 2)}
                  /year)
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box display="flex" flexDirection="row">
          <Box sx={(theme) => ({ width: theme.spacing(20) })}>
            {isDirty || loading ? (
              <Button
                fullWidth
                type="button"
                data-testid="get-price-button"
                onClick={submit(handleSubmit)}
                loading={loading}
                disabled={!isValid || loading}>
                Get price
              </Button>
            ) : (
              <Button
                fullWidth
                type="button"
                data-testid="add-item-button"
                disabled={!isValid || !!error}
                sx={{
                  fontSize: !currentItem
                    ? { xs: "1rem", md: "1.25rem" }
                    : "1.25rem",
                }}
                onClick={handleAddItem}>
                {!!currentItem ? "Save" : "Add other structure"}
              </Button>
            )}
          </Box>
          <Box
            sx={(theme) => ({
              marginLeft: theme.spacing(2),
              width: theme.spacing(20),
            })}>
            <Button
              onClick={() => {
                abortController.current.abort();
                abortController.current = new AbortController();
                changeFormVisibilityAndCloseModal(true);
              }}
              variant="outlined"
              type="button"
              fullWidth>
              Cancel
            </Button>
          </Box>
        </Box>
      </Stack>
    </Form>
  );
}

export { MineSubsidenceScheduledAddOrEditOtherStructure };
